/**
 * @file     useOwnerListingSaves.ts
 * @readme   ./CLAUDE.md
 * @purpose  Save and Add to list for owner listings. Signed in, the list is
 *           the account's saved owner listings (saved_owner_listings through
 *           POST /api/mls/saved with an ownerListingId), so they show in the
 *           dashboard's Saved list in their own group. Signed out, saves wait
 *           on this device (like the MLS liked list) and move to the account on
 *           the next signed-in visit. One store, so the Save icon and Add to
 *           list never disagree.
 */
'use client';

import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { useSupabaseUser } from '@/hooks/useSupabaseUser';

/** Device list for signed-out saves; moved to the account after sign-in. */
export const OWNER_SAVES_LOCAL_KEY = 'relta.ownerListingSaves.v1';

const listeners = new Set<() => void>();
let ids: string[] = [];
let loadedFor: string | null = null;
let localRead = false;

function emit() {
  for (const listener of listeners) listener();
}

function setIds(next: string[]) {
  ids = next;
  emit();
}

function readLocal(): string[] {
  try {
    const raw = window.localStorage.getItem(OWNER_SAVES_LOCAL_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // Blocked storage: signed-out saves last this page view only.
    return [];
  }
}

function writeLocal(next: string[]) {
  try {
    if (next.length === 0) window.localStorage.removeItem(OWNER_SAVES_LOCAL_KEY);
    else window.localStorage.setItem(OWNER_SAVES_LOCAL_KEY, JSON.stringify(next));
  } catch {
    // Quota or private mode: the in-memory list still holds this visit.
  }
}

async function post(action: 'save' | 'unsave', ownerListingId: string): Promise<boolean> {
  try {
    const res = await secureFetch('/api/mls/saved', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ action, ownerListingId }),
    });
    if (res.ok) return true;
    // 404: the listing is no longer live. Anything else is unexpected.
    if (res.status !== 404) {
      reportSilentFailure(new Error(`saved owner listing ${action} ${res.status}`), { area: 'rentals/saved-owner', route: '/api/mls/saved' });
    }
    return false;
  } catch (err) {
    reportSilentFailure(err, { area: 'rentals/saved-owner', route: '/api/mls/saved' });
    return false;
  }
}

/** Load the account's ids once per user, first moving any device saves up. */
async function loadForUser(userId: string) {
  loadedFor = userId;
  const pending = readLocal();
  if (pending.length > 0) {
    await Promise.all(pending.map((id) => post('save', id)));
    writeLocal([]);
  }
  try {
    const res = await fetch('/api/mls/saved?source=owner', { cache: 'no-store' });
    if (!res.ok) throw new Error(`saved owner listings ${res.status}`);
    const body = (await res.json()) as { ownerListingIds?: unknown };
    if (loadedFor !== userId) return;
    setIds(Array.isArray(body.ownerListingIds) ? body.ownerListingIds.filter((x): x is string => typeof x === 'string') : []);
  } catch (err) {
    reportSilentFailure(err, { area: 'rentals/saved-owner', route: '/api/mls/saved' });
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => ids;
const EMPTY: string[] = [];

/** Test seam: forget the loaded list. */
export function resetOwnerListingSaves() {
  ids = [];
  loadedFor = null;
  localRead = false;
  emit();
}

export function useOwnerListingSaved(id: string): {
  saved: boolean;
  /** Returns 'signed_out' when the save is held on this device. */
  toggle: () => 'account' | 'signed_out';
} {
  const { user } = useSupabaseUser();
  const userId = user?.id ?? null;
  const list = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);

  useEffect(() => {
    if (userId) {
      if (loadedFor !== userId) void loadForUser(userId);
    } else if (!localRead) {
      localRead = true;
      loadedFor = null;
      setIds(readLocal());
    }
  }, [userId]);

  const saved = list.includes(id);
  const toggle = useCallback(() => {
    const was = ids.includes(id);
    const next = was ? ids.filter((x) => x !== id) : [...ids, id];
    setIds(next);
    if (!userId) {
      writeLocal(next);
      return 'signed_out' as const;
    }
    void post(was ? 'unsave' : 'save', id).then((ok) => {
      // Roll back a save the server refused (the listing ended meanwhile).
      if (!ok && !was) setIds(ids.filter((x) => x !== id));
    });
    return 'account' as const;
  }, [id, userId]);

  return { saved, toggle };
}
